package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.heelion.farmafreelance.models.DueFattoriAvvio
import com.heelion.farmafreelance.models.DueFattoriStato
import kotlinx.coroutines.launch

/**
 * Secondo passaggio del login, quando l'utente ha attivato la verifica in due passaggi: il codice
 * a 6 cifre dell'app di autenticazione, oppure uno dei codici di emergenza.
 */
@Composable
fun CodiceLoginDialog(viewModel: AuthViewModel) {
    var codice by remember { mutableStateOf("") }
    var errore by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Dialog(onDismissRequest = { viewModel.annullaCodiceLogin() }) {
        Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.padding(8.dp)) {
            Column(modifier = Modifier.padding(24.dp).width(320.dp)) {
                Text("Verifica in due passaggi", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                Spacer(Modifier.height(12.dp))
                Text(
                    "Apri l'app di autenticazione (es. Google Authenticator) e scrivi il codice di FarmaFreelance. " +
                        "Senza telefono puoi usare uno dei codici di emergenza.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(Modifier.height(12.dp))
                OutlinedTextField(
                    value = codice,
                    onValueChange = { codice = it.take(9) },
                    label = { Text("Codice") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                errore?.let { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
                Spacer(Modifier.height(16.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = { viewModel.annullaCodiceLogin() }) { Text("Annulla") }
                    Spacer(Modifier.width(8.dp))
                    Button(
                        enabled = !isLoading && codice.isNotBlank(),
                        onClick = {
                            isLoading = true
                            errore = null
                            scope.launch {
                                errore = viewModel.confermaCodiceLogin(codice)
                                isLoading = false
                            }
                        }
                    ) {
                        if (isLoading) CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp, color = Color.White)
                        else Text("Entra")
                    }
                }
            }
        }
    }
}

/**
 * Impostazioni → verifica in due passaggi, facoltativa: si accende collegando un'app di
 * autenticazione e si spegne con password + codice.
 */
@Composable
fun SezioneDueFattori(viewModel: AuthViewModel) {
    var stato by remember { mutableStateOf<DueFattoriStato?>(null) }
    var avvio by remember { mutableStateOf<DueFattoriAvvio?>(null) }
    var codiciEmergenza by remember { mutableStateOf<List<String>?>(null) }
    var chiediDisattiva by remember { mutableStateOf(false) }
    var messaggio by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) { stato = viewModel.statoDueFattori() }

    Text(
        text = "Verifica in due passaggi",
        style = MaterialTheme.typography.titleMedium,
        color = MaterialTheme.colorScheme.primary,
        modifier = Modifier.padding(bottom = 8.dp)
    )
    Card(modifier = Modifier.fillMaxWidth(), elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)) {
        Column(modifier = Modifier.padding(16.dp)) {
            val attiva = stato?.attiva == true
            Row(verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(if (attiva) "Attiva" else "Non attiva", fontWeight = FontWeight.Bold)
                    Text(
                        if (attiva) "All'accesso, dopo la password, ti chiediamo il codice dell'app di autenticazione. " +
                            "Codici di emergenza rimasti: ${stato?.codiciEmergenzaRimasti}."
                        else "Oltre alla password, all'accesso servirà un codice dell'app di autenticazione sul tuo telefono " +
                            "(Google Authenticator, Microsoft Authenticator, Aegis…).",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                Switch(
                    checked = attiva || avvio != null,
                    enabled = stato != null,
                    onCheckedChange = { accendi ->
                        messaggio = null
                        if (!accendi) {
                            if (attiva) chiediDisattiva = true else avvio = null
                        } else {
                            scope.launch {
                                avvio = viewModel.iniziaDueFattori()
                                if (avvio == null) messaggio = "Non è stato possibile iniziare, riprova."
                            }
                        }
                    }
                )
            }
            messaggio?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = MaterialTheme.colorScheme.primary, style = MaterialTheme.typography.bodySmall)
            }
            avvio?.let { dati ->
                Spacer(Modifier.height(12.dp))
                AttivazioneDueFattori(dati, viewModel) { codici ->
                    avvio = null
                    codiciEmergenza = codici
                    scope.launch { stato = viewModel.statoDueFattori() }
                }
            }
        }
    }

    codiciEmergenza?.let { codici ->
        CodiciEmergenzaDialog(codici) { codiciEmergenza = null }
    }
    if (chiediDisattiva) {
        DisattivaDueFattoriDialog(
            viewModel,
            onFatto = {
                chiediDisattiva = false
                messaggio = "Verifica in due passaggi disattivata."
                scope.launch { stato = viewModel.statoDueFattori() }
            },
            onAnnulla = { chiediDisattiva = false }
        )
    }
}

@Composable
private fun AttivazioneDueFattori(dati: DueFattoriAvvio, viewModel: AuthViewModel, onAttivata: (List<String>) -> Unit) {
    var codice by remember { mutableStateOf("") }
    var errore by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current
    val appunti = LocalClipboardManager.current
    val scope = rememberCoroutineScope()

    Text("1. Aggiungi FarmaFreelance all'app di autenticazione", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
    Text(
        "Dal telefono tocca \"Apri nell'app\". Altrimenti, nell'app scegli \"Inserisci una chiave di configurazione\" e scrivi questa chiave:",
        style = MaterialTheme.typography.bodySmall
    )
    SelectionContainer {
        Text(
            dati.segreto.chunked(4).joinToString(" "),
            fontFamily = FontFamily.Monospace,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(vertical = 8.dp)
        )
    }
    Row {
        OutlinedButton(onClick = {
            // Senza un'app di autenticazione installata il link non si apre: resta la chiave da scrivere.
            try {
                uriHandler.openUri(dati.uri)
            } catch (e: Exception) {
                errore = "Nessuna app di autenticazione trovata: installane una o scrivi la chiave a mano."
            }
        }) { Text("Apri nell'app") }
        Spacer(Modifier.width(8.dp))
        TextButton(onClick = { appunti.setText(AnnotatedString(dati.segreto)) }) { Text("Copia chiave") }
    }
    Spacer(Modifier.height(12.dp))
    Text("2. Scrivi il codice a 6 cifre che compare nell'app", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
    Row(verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
        OutlinedTextField(
            value = codice,
            onValueChange = { codice = it.filter { c -> c.isDigit() }.take(6) },
            label = { Text("Codice") },
            singleLine = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.weight(1f),
            shape = RoundedCornerShape(12.dp)
        )
        Spacer(Modifier.width(8.dp))
        Button(
            enabled = !isLoading && codice.length == 6,
            onClick = {
                isLoading = true
                errore = null
                scope.launch {
                    val codici = viewModel.confermaDueFattori(codice)
                    isLoading = false
                    if (codici != null) onAttivata(codici)
                    else errore = "Codice non valido: controlla che l'ora del telefono sia giusta e riprova."
                }
            }
        ) { Text("Attiva") }
    }
    errore?.let { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
}

/** Mostrati una volta sola: dopo, nel server resta solo la loro impronta. */
@Composable
private fun CodiciEmergenzaDialog(codici: List<String>, onChiudi: () -> Unit) {
    val appunti = LocalClipboardManager.current
    AlertDialog(
        onDismissRequest = {}, // si chiude solo confermando di averli salvati
        title = { Text("Salva i codici di emergenza") },
        text = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                Text(
                    "Verifica in due passaggi attiva. Se perdi il telefono puoi entrare con uno di questi codici " +
                        "(ognuno vale una volta). Conservali in un posto sicuro: non li vedrai più.",
                    style = MaterialTheme.typography.bodySmall
                )
                Spacer(Modifier.height(12.dp))
                SelectionContainer {
                    Text(codici.joinToString("\n"), fontFamily = FontFamily.Monospace, fontWeight = FontWeight.Bold)
                }
            }
        },
        confirmButton = { Button(onClick = onChiudi) { Text("Li ho salvati") } },
        dismissButton = { TextButton(onClick = { appunti.setText(AnnotatedString(codici.joinToString("\n"))) }) { Text("Copia") } }
    )
}

@Composable
private fun DisattivaDueFattoriDialog(viewModel: AuthViewModel, onFatto: () -> Unit, onAnnulla: () -> Unit) {
    var password by remember { mutableStateOf("") }
    var codice by remember { mutableStateOf("") }
    var errore by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    AlertDialog(
        onDismissRequest = onAnnulla,
        title = { Text("Disattivare la verifica in due passaggi?") },
        text = {
            Column {
                Text("Per sicurezza servono la password e un codice dell'app (o di emergenza).", style = MaterialTheme.typography.bodySmall)
                Spacer(Modifier.height(8.dp))
                CampoPassword(value = password, onValueChange = { password = it }, label = { Text("Password") }, modifier = Modifier.fillMaxWidth())
                Spacer(Modifier.height(8.dp))
                OutlinedTextField(
                    value = codice,
                    onValueChange = { codice = it.take(9) },
                    label = { Text("Codice") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                errore?.let { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
            }
        },
        confirmButton = {
            Button(
                enabled = !isLoading && password.isNotBlank() && codice.isNotBlank(),
                onClick = {
                    isLoading = true
                    errore = null
                    scope.launch {
                        val ok = viewModel.disattivaDueFattori(password, codice)
                        isLoading = false
                        if (ok) onFatto() else errore = "Password o codice non validi."
                    }
                }
            ) { Text("Disattiva") }
        },
        dismissButton = { TextButton(onClick = onAnnulla) { Text("Annulla") } }
    )
}
